import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { AcademicAdmissionFunctions } from './AcademicAdmissionFunctions'
import {
    createLoadExistingData,
    createHandleSubmitSuccess,
    createOnSubmit
} from './useAcademicAdmissionFormHelpers'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { useAuthHook, useInscriptionPreload } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { AcademicAdmission, FormsGroupsProps } from '@/types/forms/'
import { useFormProgress } from '@/utils'

export const useAcademicAdmissionForm = ({
    onNext
}: Pick<FormsGroupsProps, 'onNext'>) => {
    const { control, handleSubmit, reset, watch, setValue } =
        useForm<AcademicAdmission>({
            defaultValues: {
                program: '',
                admissionType: '',
                campus: '',
                studyModality: '',
                startPeriod: '',
                educationModality: '',
                acceptInstructiveDifferentPeriod: undefined
            }
        })

    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { idAsesor, updateStudyModality } = useAuthStore()
    const { idAspirant, idInscription } = useAuthHook()
    const { updateIdInscription } = useAuthStore()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { showLoader, hideLoader } = useProviderLoader()

    const {
        preloadData,
        isLoading: isPreloading,
        clearPreloadData,
        hasPreloadData
    } = useInscriptionPreload()

    const progress = useFormProgress<AcademicAdmission>({
        watch,
        excludedFields: [
            'idInscription',
            'id_inscription',
            'idInscription_sap',
            'id_aspirant',
            'isStatusRejectedValid',
            //'hasCategoriaAdmision'
        ],
        conditionalFields: [
            {
                field: 'acceptInstructiveDifferentPeriod',
                dependsOn: 'startPeriod',
                condition: (value, formData) => {
                    // Solo es requerido si el cicloId NO es 4, 7 o 15
                    // Y si la modalidad NO es "1" (Convenio Regional)
                    const id = Number(value)
                    return (
                        value !== '' &&
                        ![4, 7, 15].includes(id) &&
                        formData.educationModality !== '1'
                    )
                }
            }
            /* ,
            {
                field: 'categoriaAdmision',
                dependsOn: 'hasCategoriaAdmision',
                condition: true
            } */
        ]
    })

    const { fetchModality, fetchAcademicCycleWithInitialDate } =
        AcademicAdmissionFunctions()

    const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false)
    const [hasExistingData, setHasExistingData] = useState(false)

    // Extraer helpers
    const loadExistingData = createLoadExistingData(
        showLoader,
        hideLoader,
        aspirantsApi,
        inscriptionsApi,
        String(idAspirant),
        idInscription,
        reset,
        setHasExistingData,
        setHasLoadedExistingData,
        updateStudyModality
    )

    useEffect(() => {
        console.log('🔧 Debug useAcademicAdmissionForm:', {
            idAspirant,
            idInscription,
            hasLoadedExistingData
        })

        if (idAspirant && !hasLoadedExistingData) {
            loadExistingData()
        }
    }, [idAspirant, hasLoadedExistingData, loadExistingData])

    useEffect(() => {
        if (
            hasLoadedExistingData &&
            !hasExistingData &&
            hasPreloadData &&
            preloadData &&
            !isPreloading
        ) {
            reset(preloadData as any)
        }
    }, [
        hasLoadedExistingData,
        hasExistingData,
        hasPreloadData,
        preloadData,
        isPreloading,
        reset
    ])

    const handleSubmitSuccess = createHandleSubmitSuccess(
        updateIdInscription,
        aspirantsApi,
        String(idAspirant),
        idAsesor || 0,
        clearPreloadData,
        onNext,
        updateStudyModality
    )

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const onSubmit: SubmitHandler<AcademicAdmission> = createOnSubmit(
        String(idAspirant),
        idInscription,
        hasExistingData,
        showLoader,
        hideLoader,
        fetchModality,
        fetchAcademicCycleWithInitialDate,
        inscriptionsApi,
        resourcesApi,
        idAsesor || 0,
        handleSubmitSuccess,
        aspirantsApi
    )

    return {
        control,
        handleSubmit,
        watch,
        setValue,
        onSubmit,
        progress,
        isPreloading,
        hasPreloadData,
        preloadData,
        hasExistingData,
        hasLoadedExistingData,
        idAspirant,
        // Programa aplicado desde la precarga (no hay datos guardados)
        preloadedProgram:
            hasLoadedExistingData &&
            !hasExistingData &&
            hasPreloadData &&
            !isPreloading
                ? preloadData?.program
                : undefined
    }
}
